import React from 'react';
import PropTypes from 'prop-types';
import { Link, Icon } from '@resy/ui-kitchen';
import { sanitizeString } from '../../../../../../../ui-kitchen/src/utils/sanitizeString';
import ExternalLink from '../../../../../../../ui-kitchen/src/svg/icons/external-link.svg';
import { handleMktAnalytics, deepMergeDefined, convertToPlainText } from '../../AmexUtils';
import { CTA_EVENT_NAME } from '../../constants';
import { contentShape } from '../AmexTabbedCarouselRefresh.props';
import './LayoutCreditRefresh.scss';

const LayoutCreditRefresh = ({
  awsFolderName,
  baseRequestUrl,
  isStandalone,
  content,
  id,
  specificCardContent,
  tabHeading,
  tabThumbImage,
  matchMedia,
  Analytics,
}) => {
  let mergedSpecificCardConfig = structuredClone(content);
  if (isStandalone) mergedSpecificCardConfig = deepMergeDefined(content, specificCardContent);

  const {
    title,
    copy,
    fine_copy: finePrint,
    image: {
      desktop: desktopImg = {},
      mobile: mobileImg = {},
    } = {},
    cta: {
      label: ctaLabel,
      aria_label: ariaLabel,
      url: ctaUrl,
    } = {},
    section_title: sectionTitle,
    section_intro: sectionIntro,
  } = mergedSpecificCardConfig;

  const specificParam = {
    ...(!isStandalone && { tab_item: convertToPlainText(tabHeading) }),
    action_name: `${ctaLabel} Click`,
  };

  const TitleTag = isStandalone ? 'h2' : 'h3';
  const HeadingTag = isStandalone ? 'h3' : 'h4';
  const cardImagePrefix = `${baseRequestUrl}${awsFolderName}/carouselImages/tabBgImages/`;
  const thumbImagePath = tabThumbImage
    ? `${baseRequestUrl}${awsFolderName}/images/${tabThumbImage}`
    : '';

  const sanitizedHeading = sanitizeString(tabHeading);
  const sanitizedTitle = sanitizeString(title);
  const sanitizedCopy = sanitizeString(copy);
  const sanitizedFinePrint = sanitizeString(finePrint);

  const cardImage = matchMedia?.lg ? desktopImg : mobileImg;
  const hasCardImage = !!(cardImage?.regular && cardImage?.retina);

  const renderLayoutCredit = () => (
    <div className="LayoutCreditRefresh__inner">
      <div className="LayoutCreditRefresh__content">
        <div className="LayoutCreditRefresh__copy">
          {title && (
            <TitleTag
              className="LayoutCreditRefresh__title"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: sanitizedTitle }}
            />
          )}
          {tabHeading && (
            <HeadingTag
              className="LayoutCreditRefresh__heading"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: sanitizedHeading }}
            />
          )}
          {copy && (
            <p
              className="LayoutCreditRefresh__text"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: sanitizedCopy }}
            />
          )}
          {finePrint && (
            <p
              className="LayoutCreditRefresh__fine-print"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: sanitizedFinePrint }}
            />
          )}
        </div>
        {(ctaLabel && ctaUrl) && (
          <Link
            href={ctaUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="LayoutCreditRefresh__cta"
            onClick={() => handleMktAnalytics(Analytics?.event, CTA_EVENT_NAME, specificParam)}
            aria-label={ariaLabel}
          >
            {ctaLabel}
            <Icon component={ExternalLink} role="presentation" />
          </Link>
        )}
      </div>

      <div className="LayoutCreditRefresh__images">
        {tabThumbImage && (
          <img
            className="LayoutCreditRefresh__card-art"
            src={thumbImagePath}
            alt=""
          />
        )}
        {hasCardImage && (
          <img
            className="LayoutCreditRefresh__card-image"
            src={`${cardImagePrefix}${cardImage.regular}`}
            srcSet={`${cardImagePrefix}${cardImage.regular} 1x, ${cardImagePrefix}${cardImage.retina} 2x`}
            alt=""
          />
        )}
      </div>
    </div>
  );

  const renderSectionTitle = () => (
    <>
      {sectionTitle && (
        <h2
          id={id}
          className="LayoutCreditRefresh__section--title"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: sanitizeString(sectionTitle) }}
        />
      )}
      {sectionIntro && (
        <p
          className="LayoutCreditRefresh__section--intro"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: sanitizeString(sectionIntro) }}
        />
      )}
    </>
  );

  return isStandalone ? (
    <section className="LayoutCreditRefresh standalone">
      <div className="container">
        {renderSectionTitle()}
        {renderLayoutCredit()}
      </div>
    </section>
  ) : (
    <div className="LayoutCreditRefresh">
      {renderLayoutCredit()}
    </div>
  );
};

LayoutCreditRefresh.propTypes = {
  awsFolderName: PropTypes.string.isRequired,
  baseRequestUrl: PropTypes.string.isRequired,
  isStandalone: PropTypes.bool.isRequired,
  content: contentShape,
  specificCardContent: contentShape,
  tabHeading: PropTypes.string,
  id: PropTypes.string,
  tabThumbImage: PropTypes.string,
  matchMedia: PropTypes.shape({
    lg: PropTypes.bool,
    xl: PropTypes.bool,
  }),
  Analytics: PropTypes.shape({
    event: PropTypes.func,
  }),
};

LayoutCreditRefresh.defaultProps = {
  content: {},
  specificCardContent: {},
  tabHeading: '',
  id: '',
  tabThumbImage: '',
  matchMedia: {},
  Analytics: {},
};

export default LayoutCreditRefresh;
